import React, { useMemo, useState, Fragment } from 'react';
import { PlusIcon, Trash2Icon, ChevronDownIcon, GitBranchIcon, XIcon, ArrowLeftIcon, ArrowDownIcon, ZapIcon, SettingsIcon, WorkflowIcon } from 'lucide-react';
import { FormBlock, FormLogicRule, LogicCondition, LogicAction, LogicConditionType, LogicActionType, generateId } from './types';
interface LogicRulesEditorProps {
  blocks: FormBlock[];
  rules: FormLogicRule[];
  onChange: (rules: FormLogicRule[]) => void;
  onClose: () => void;
}
const CONDITION_LABELS: Record<LogicConditionType, string> = {
  equals: 'Equals',
  not_equals: 'Does not equal',
  contains: 'Contains',
  not_contains: 'Does not contain',
  is_empty: 'Is empty',
  is_not_empty: 'Is not empty',
  greater_than: 'Greater than',
  less_than: 'Less than',
  one_of: 'Is one of'
};
const ACTION_LABELS: Record<LogicActionType, string> = {
  show_block: 'Show field',
  hide_block: 'Hide field',
  require_block: 'Make required',
  optional_block: 'Make optional',
  set_field_value: 'Set field value',
  jump_to_block: 'Jump to field',
  show_section: 'Show section',
  hide_section: 'Hide section',
  filter_options: 'Filter dropdown options'
};
const NODE_COLORS: Record<string, string> = {
  text: '#10b981',
  textarea: '#10b981',
  url: '#10b981',
  email: '#3b82f6',
  phone: '#8b5cf6',
  number: '#f59e0b',
  select: '#1f2937',
  radio: '#1f2937',
  checkbox: '#1f2937',
  date: '#ec4899',
  file: '#f97316',
  section: '#6366f1',
  subform: '#6366f1',
  submit: '#ef4444',
  consent: '#14b8a6',
  hidden: '#9ca3af',
  heading: '#6b7280',
  paragraph: '#6b7280',
  divider: '#d1d5db',
  spacer: '#d1d5db',
  image: '#d1d5db'
};
const RULE_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];
export function LogicRulesEditor({
  blocks,
  rules,
  onChange,
  onClose
}: LogicRulesEditorProps) {
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(rules[0]?.id || null);
  const [rightTab, setRightTab] = useState<'diagram' | 'settings'>('diagram');
  const triggerBlocks = blocks.filter(b => !['heading', 'paragraph', 'divider', 'spacer', 'image', 'submit', 'subform'].includes(b.type));
  const selectedRule = rules.find(r => r.id === selectedRuleId) || null;
  // ── CRUD helpers ──
  const addRule = () => {
    const newRule: FormLogicRule = {
      id: generateId(),
      name: `Rule ${rules.length + 1}`,
      conditionMatch: 'all',
      conditions: [{
        id: generateId(),
        fieldId: triggerBlocks[0]?.id || '',
        operator: 'equals',
        value: ''
      }],
      actions: [{
        id: generateId(),
        type: 'show_block',
        targetId: blocks[0]?.id || ''
      }]
    };
    onChange([...rules, newRule]);
    setSelectedRuleId(newRule.id);
  };
  const updateRule = (id: string, updates: Partial<FormLogicRule>) => {
    onChange(rules.map(r => r.id === id ? {
      ...r,
      ...updates
    } : r));
  };
  const deleteRule = (id: string) => {
    onChange(rules.filter(r => r.id !== id));
    if (selectedRuleId === id) {
      const remaining = rules.filter(r => r.id !== id);
      setSelectedRuleId(remaining[0]?.id || null);
    }
  };
  const addCondition = (ruleId: string) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    updateRule(ruleId, {
      conditions: [...rule.conditions, {
        id: generateId(),
        fieldId: triggerBlocks[0]?.id || '',
        operator: 'equals',
        value: ''
      }]
    });
  };
  const updateCondition = (ruleId: string, conditionId: string, updates: Partial<LogicCondition>) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    updateRule(ruleId, {
      conditions: rule.conditions.map(c => c.id === conditionId ? {
        ...c,
        ...updates
      } : c)
    });
  };
  const deleteCondition = (ruleId: string, conditionId: string) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    updateRule(ruleId, {
      conditions: rule.conditions.filter(c => c.id !== conditionId)
    });
  };
  const addAction = (ruleId: string) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    updateRule(ruleId, {
      actions: [...rule.actions, {
        id: generateId(),
        type: 'show_block',
        targetId: blocks[0]?.id || ''
      }]
    });
  };
  const updateAction = (ruleId: string, actionId: string, updates: Partial<LogicAction>) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    updateRule(ruleId, {
      actions: rule.actions.map(a => a.id === actionId ? {
        ...a,
        ...updates
      } : a)
    });
  };
  const deleteAction = (ruleId: string, actionId: string) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    updateRule(ruleId, {
      actions: rule.actions.filter(a => a.id !== actionId)
    });
  };
  const getBlockLabel = (id: string) => {
    const b = blocks.find(bl => bl.id === id);
    if (!b) return '(unknown)';
    if (b.type === 'section') return b.sectionTitle || b.label || 'Section';
    return b.label || b.name || b.type;
  };
  return <div className="fixed inset-0 z-50 bg-white flex flex-col" data-id="element-1507">
      {/* ── Top Bar ── */}
      <div className="h-14 border-b border-gray-200 px-4 flex items-center justify-between flex-shrink-0 bg-white" data-id="element-1508">
        <div className="flex items-center gap-3" data-id="element-1509">
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors" data-id="element-1510">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-1511" />
          </button>
          <div className="h-5 w-px bg-gray-200" data-id="element-1512" />
          <GitBranchIcon className="h-5 w-5 text-purple-500" data-id="element-1513" />
          <h1 className="text-base font-semibold text-gray-900" data-id="element-1514">
            Logic Rules Editor
          </h1>
          {rules.length > 0 && <span className="text-xs font-medium px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full" data-id="element-1515">
              {rules.length} rule{rules.length !== 1 ? 's' : ''}
            </span>}
        </div>
        <button onClick={onClose} className="p-2 rounded-md hover:bg-gray-100 text-gray-400 transition-colors" data-id="element-1516">
          <XIcon className="h-5 w-5" data-id="element-1517" />
        </button>
      </div>

      {/* ── Main Content ── */}
      <div className="flex-1 flex overflow-hidden" data-id="element-1518">
        {/* ── LEFT: Rules Sidebar ── */}
        <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50/70 flex flex-col" data-id="element-1519">
          <div className="p-4 pb-3 flex items-center justify-between border-b border-gray-200" data-id="element-1520">
            <div className="flex items-center gap-2" data-id="element-1521">
              <span className="text-sm font-semibold text-gray-900" data-id="element-1522">Rules</span>
              <span className="text-xs text-gray-400" data-id="element-1523">{rules.length}</span>
            </div>
            <button onClick={addRule} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors" data-id="element-1524">
              <PlusIcon className="h-3.5 w-3.5" data-id="element-1525" />
              Add
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-id="element-1526">
            {rules.length === 0 ? <div className="text-center py-12 px-4" data-id="element-1527">
                <GitBranchIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" data-id="element-1528" />
                <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-1529">
                  No rules yet
                </p>
                <p className="text-xs text-gray-400 mb-4" data-id="element-1530">
                  Create your first conditional logic rule
                </p>
                <button onClick={addRule} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors" data-id="element-1531">
                  <PlusIcon className="h-3.5 w-3.5" data-id="element-1532" />
                  Create Rule
                </button>
              </div> : rules.map((rule, idx) => {
            const isSelected = selectedRuleId === rule.id;
            const ruleColor = RULE_COLORS[idx % RULE_COLORS.length];
            return <button key={rule.id} onClick={() => setSelectedRuleId(rule.id)} className={`w-full text-left rounded-lg p-3 transition-all ${isSelected ? 'bg-white shadow-sm ring-1 ring-gray-200' : 'hover:bg-white/60'}`} style={{
              borderLeft: isSelected ? `3px solid ${ruleColor}` : '3px solid transparent'
            }} data-id="element-1533">
                    <div className="flex items-start justify-between gap-2" data-id="element-1534">
                      <div className="min-w-0" data-id="element-1535">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-1536">
                          {rule.name}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1.5" data-id="element-1537">
                          <span className="text-[10px] font-medium px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded" data-id="element-1538">
                            {rule.conditions.length} IF
                          </span>
                          <span className="text-[10px] font-medium px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded" data-id="element-1539">
                            {rule.actions.length} THEN
                          </span>
                        </div>
                      </div>
                      <button onClick={e => {
                  e.stopPropagation();
                  deleteRule(rule.id);
                }} className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all flex-shrink-0" style={{
                  opacity: isSelected ? 1 : undefined
                }} data-id="element-1540">
                        <Trash2Icon className="h-3.5 w-3.5" data-id="element-1541" />
                      </button>
                    </div>
                  </button>;
          })}
          </div>
        </div>

        {/* ── CENTER: Rule Editor ── */}
        <div className="flex-1 overflow-y-auto bg-gray-50/30" data-id="element-1542">
          {!selectedRule ? <div className="flex items-center justify-center h-full" data-id="element-1543">
              <div className="text-center px-8" data-id="element-1544">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4" data-id="element-1545">
                  <ZapIcon className="h-8 w-8 text-purple-400" data-id="element-1546" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1547">
                  Select a Rule
                </h3>
                <p className="text-sm text-gray-500 max-w-xs mx-auto" data-id="element-1548">
                  {rules.length === 0 ? 'Create your first rule to add conditional logic to your form.' : 'Select a rule from the sidebar to edit its conditions and actions.'}
                </p>
                {rules.length === 0 && <button onClick={addRule} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors" data-id="element-1549">
                    <PlusIcon className="h-4 w-4" data-id="element-1550" />
                    Create First Rule
                  </button>}
              </div>
            </div> : <div className="max-w-2xl mx-auto p-8" data-id="element-1551">
              {/* Rule Name */}
              <div className="mb-8" data-id="element-1552">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1553">
                  Rule Name
                </label>
                <input type="text" value={selectedRule.name} onChange={e => updateRule(selectedRule.id, {
              name: e.target.value
            })} className="block w-full text-lg font-semibold text-gray-900 border-0 border-b-2 border-gray-200 focus:border-purple-500 focus:ring-0 bg-transparent pb-2 px-0 placeholder:text-gray-300" placeholder="Enter rule name..." data-id="element-1554" />
              </div>

              {/* ── IF Section ── */}
              <div className="mb-6" data-id="element-1555">
                <div className="flex items-center justify-between mb-4" data-id="element-1556">
                  <div className="flex items-center gap-3" data-id="element-1557">
                    <span className="text-xs font-bold text-gray-700 bg-gray-200 px-2.5 py-1 rounded-md uppercase tracking-wide" data-id="element-1558">
                      IF
                    </span>
                    {selectedRule.conditions.length > 1 && <select value={selectedRule.conditionMatch} onChange={e => updateRule(selectedRule.id, {
                  conditionMatch: e.target.value as 'all' | 'any'
                })} className="text-sm border-gray-300 rounded-md shadow-sm py-1.5 pl-3 pr-8 focus:ring-purple-500 focus:border-purple-500" data-id="element-1559">
                        <option value="all" data-id="element-1560">ALL conditions match</option>
                        <option value="any" data-id="element-1561">ANY condition matches</option>
                      </select>}
                  </div>
                  <button onClick={() => addCondition(selectedRule.id)} className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors" data-id="element-1562">
                    <PlusIcon className="h-3.5 w-3.5" data-id="element-1563" />
                    Add Condition
                  </button>
                </div>

                <div className="space-y-3" data-id="element-1564">
                  {selectedRule.conditions.map((condition, idx) => <Fragment key={condition.id} data-id="element-1565">
                      {idx > 0 && <div className="flex justify-center" data-id="element-1566">
                          <button type="button" onClick={() => updateRule(selectedRule.id, {
                    conditionMatch: selectedRule.conditionMatch === 'all' ? 'any' : 'all'
                  })} className="inline-flex items-center px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full cursor-pointer transition-colors select-none bg-purple-100 text-purple-700 hover:bg-purple-200" title={`Click to switch to ${selectedRule.conditionMatch === 'all' ? 'OR' : 'AND'}`} data-id="element-1567">
                            {selectedRule.conditionMatch === 'all' ? 'AND' : 'OR'}
                          </button>
                        </div>}
                      <div className="bg-white rounded-xl border border-gray-200 p-4 relative group" data-id="element-1568">
                        {selectedRule.conditions.length > 1 && <button onClick={() => deleteCondition(selectedRule.id, condition.id)} className="absolute top-3 right-3 p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100" data-id="element-1569">
                            <XIcon className="h-4 w-4" data-id="element-1570" />
                          </button>}
                        <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-1571">
                          <div data-id="element-1572">
                            <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-1573">
                              Field
                            </label>
                            <select value={condition.fieldId} onChange={e => updateCondition(selectedRule.id, condition.id, {
                        fieldId: e.target.value
                      })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm py-2 px-3" data-id="element-1574">
                              <option value="" disabled data-id="element-1575">
                                Select field...
                              </option>
                              {triggerBlocks.map(b => <option key={b.id} value={b.id} data-id="element-1576">
                                  {b.label || b.name}
                                </option>)}
                            </select>
                          </div>
                          <div data-id="element-1577">
                            <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-1578">
                              Operator
                            </label>
                            <select value={condition.operator} onChange={e => updateCondition(selectedRule.id, condition.id, {
                        operator: e.target.value as LogicConditionType
                      })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm py-2 px-3" data-id="element-1579">
                              {Object.entries(CONDITION_LABELS).map(([val, label]) => <option key={val} value={val} data-id="element-1580">
                                    {label}
                                  </option>)}
                            </select>
                          </div>
                        </div>
                        {condition.operator !== 'is_empty' && condition.operator !== 'is_not_empty' && <div data-id="element-1581">
                              <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-1582">
                                Value
                              </label>
                              <input type="text" value={condition.value as string} onChange={e => updateCondition(selectedRule.id, condition.id, {
                      value: e.target.value
                    })} placeholder="Enter value..." className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm py-2 px-3" data-id="element-1583" />
                            </div>}
                      </div>
                    </Fragment>)}
                </div>
              </div>

              {/* Arrow divider */}
              <div className="flex justify-center my-4" data-id="element-1584">
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-1585">
                  <ArrowDownIcon className="h-4 w-4 text-gray-400" data-id="element-1586" />
                </div>
              </div>

              {/* ── THEN Section ── */}
              <div data-id="element-1587">
                <div className="flex items-center justify-between mb-4" data-id="element-1588">
                  <span className="text-xs font-bold text-purple-800 bg-purple-100 px-2.5 py-1 rounded-md uppercase tracking-wide" data-id="element-1589">
                    THEN
                  </span>
                  <button onClick={() => addAction(selectedRule.id)} className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors" data-id="element-1590">
                    <PlusIcon className="h-3.5 w-3.5" data-id="element-1591" />
                    Add Action
                  </button>
                </div>

                <div className="space-y-3" data-id="element-1592">
                  {selectedRule.actions.map((action, idx) => <Fragment key={action.id} data-id="element-1593">
                      {idx > 0 && <div className="flex justify-center" data-id="element-1594">
                          <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider" data-id="element-1595">
                            AND
                          </span>
                        </div>}
                      <div className="bg-white rounded-xl border border-gray-200 p-4 relative group" data-id="element-1596">
                        {selectedRule.actions.length > 1 && <button onClick={() => deleteAction(selectedRule.id, action.id)} className="absolute top-3 right-3 p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100" data-id="element-1597">
                            <XIcon className="h-4 w-4" data-id="element-1598" />
                          </button>}
                        <div className="grid grid-cols-2 gap-3" data-id="element-1599">
                          <div data-id="element-1600">
                            <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-1601">
                              Action
                            </label>
                            <select value={action.type} onChange={e => updateAction(selectedRule.id, action.id, {
                        type: e.target.value as LogicActionType
                      })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm py-2 px-3" data-id="element-1602">
                              {Object.entries(ACTION_LABELS).map(([val, label]) => <option key={val} value={val} data-id="element-1603">
                                    {label}
                                  </option>)}
                            </select>
                          </div>
                          <div data-id="element-1604">
                            <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-1605">
                              Target
                            </label>
                            <select value={action.targetId} onChange={e => updateAction(selectedRule.id, action.id, {
                        targetId: e.target.value
                      })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm py-2 px-3" data-id="element-1606">
                              <option value="" disabled data-id="element-1607">
                                {action.type === 'show_section' || action.type === 'hide_section' ? 'Select section...' : 'Select field...'}
                              </option>
                              {(() => {
                          // Filter target blocks based on action type
                          if (action.type === 'show_section' || action.type === 'hide_section') {
                            return blocks.filter(b => b.type === 'section');
                          }
                          if (action.type === 'show_block' || action.type === 'hide_block') {
                            // Show/hide can target any visible block including subforms
                            return blocks.filter(b => !['heading', 'paragraph', 'divider', 'spacer', 'image'].includes(b.type));
                          }
                          if (action.type === 'filter_options') {
                            // Only select/dropdown fields can have options filtered
                            return blocks.filter(b => b.type === 'select');
                          }
                          // For set_field_value, require, optional, jump — exclude non-input types
                          return blocks.filter(b => !['heading', 'paragraph', 'divider', 'spacer', 'image', 'submit', 'section', 'subform'].includes(b.type));
                        })().map(b => <option key={b.id} value={b.id} data-id="element-1608">
                                  {b.type === 'section' ? b.sectionTitle || b.label || 'Section' : b.type === 'subform' ? b.subformName || b.label || 'Subform' : b.label || b.name || b.type}
                                </option>)}
                            </select>
                          </div>
                        </div>
                        {action.type === 'set_field_value' && <div className="mt-3" data-id="element-1609">
                            <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-1610">
                              Value
                            </label>
                            <input type="text" value={action.value || ''} onChange={e => updateAction(selectedRule.id, action.id, {
                      value: e.target.value
                    })} placeholder="Enter value..." className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm py-2 px-3" data-id="element-1611" />
                          </div>}
                        {action.type === 'filter_options' && (() => {
                    const targetBlock = blocks.find(b => b.id === action.targetId);
                    const targetOptions = targetBlock?.validation?.options || [];
                    const selectedValues = action.filterValues || [];
                    if (!targetBlock || targetBlock.type !== 'select') {
                      return <div className="mt-3 text-xs text-gray-400 italic" data-id="element-1612">
                                  Select a dropdown field above to configure
                                  option filtering.
                                </div>;
                    }
                    if (targetOptions.length === 0) {
                      return <div className="mt-3 text-xs text-amber-600 bg-amber-50 rounded-lg p-3" data-id="element-1613">
                                  The selected dropdown has no options defined.
                                  Add options to the field first.
                                </div>;
                    }
                    return <div className="mt-3" data-id="element-1614">
                                <label className="block text-[11px] font-medium text-gray-500 mb-2" data-id="element-1615">
                                  Options to show when rule matches
                                </label>
                                <div className="space-y-1.5 max-h-48 overflow-y-auto rounded-lg border border-gray-200 p-2 bg-gray-50/50" data-id="element-1616">
                                  {targetOptions.map(opt => {
                          const isChecked = selectedValues.includes(opt.value);
                          return <label key={opt.value} className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${isChecked ? 'bg-purple-50 text-purple-800' : 'hover:bg-gray-100 text-gray-700'}`} data-id="element-1617">
                                        <input type="checkbox" checked={isChecked} onChange={e => {
                              const newValues = e.target.checked ? [...selectedValues, opt.value] : selectedValues.filter(v => v !== opt.value);
                              updateAction(selectedRule.id, action.id, {
                                filterValues: newValues
                              });
                            }} className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-3.5 w-3.5" data-id="element-1618" />
                                        <span className="truncate" data-id="element-1619">
                                          {opt.label}
                                        </span>
                                        {opt.value !== opt.label && <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0" data-id="element-1620">
                                            {opt.value}
                                          </span>}
                                      </label>;
                        })}
                                </div>
                                <div className="flex items-center justify-between mt-2" data-id="element-1621">
                                  <span className="text-[10px] text-gray-400" data-id="element-1622">
                                    {selectedValues.length} of{' '}
                                    {targetOptions.length} options selected
                                  </span>
                                  <div className="flex gap-2" data-id="element-1623">
                                    <button onClick={() => updateAction(selectedRule.id, action.id, {
                            filterValues: targetOptions.map(o => o.value)
                          })} className="text-[10px] text-purple-600 hover:text-purple-800" data-id="element-1624">
                                      Select all
                                    </button>
                                    <button onClick={() => updateAction(selectedRule.id, action.id, {
                            filterValues: []
                          })} className="text-[10px] text-gray-500 hover:text-gray-700" data-id="element-1625">
                                      Clear
                                    </button>
                                  </div>
                                </div>
                              </div>;
                  })()}
                      </div>
                    </Fragment>)}
                </div>
              </div>
            </div>}
        </div>

        {/* ── RIGHT: Flow Diagram / Settings ── */}
        <div className="w-80 flex-shrink-0 border-l border-gray-200 bg-white flex flex-col" data-id="element-1626">
          <div className="flex border-b border-gray-200" data-id="element-1627">
            <button onClick={() => setRightTab('diagram')} className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-colors ${rightTab === 'diagram' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1628">
              <WorkflowIcon className="h-3.5 w-3.5" data-id="element-1629" />
              Flow Diagram
            </button>
            <button onClick={() => setRightTab('settings')} className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-colors ${rightTab === 'settings' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1630">
              <SettingsIcon className="h-3.5 w-3.5" data-id="element-1631" />
              Summary
            </button>
          </div>

          <div className="flex-1 overflow-y-auto" data-id="element-1632">
            {rightTab === 'diagram' ? <FlowDiagram blocks={blocks} rules={rules} selectedRuleId={selectedRuleId} onSelectRule={setSelectedRuleId} data-id="element-1633" /> : <RulesSummary rules={rules} blocks={blocks} getBlockLabel={getBlockLabel} data-id="element-1634" />}
          </div>
        </div>
      </div>
    </div>;
}
// ── Flow Diagram ──
function FlowDiagram({
  blocks,
  rules,
  selectedRuleId,
  onSelectRule
}: {
  blocks: FormBlock[];
  rules: FormLogicRule[];
  selectedRuleId: string | null;
  onSelectRule: (id: string) => void;
}) {
  const visibleBlocks = blocks.filter(b => !['divider', 'spacer', 'image', 'heading', 'paragraph'].includes(b.type));
  const nodeWidth = 190;
  const nodeHeight = 40;
  const nodeGapY = 16;
  const paddingX = 20;
  const paddingY = 20;
  const totalHeight = paddingY * 2 + visibleBlocks.length * (nodeHeight + nodeGapY) - nodeGapY;
  const svgWidth = 340;
  const svgHeight = Math.max(totalHeight, 300);
  const nodePositions = useMemo(() => {
    const map: Record<string, {
      x: number;
      y: number;
    }> = {};
    visibleBlocks.forEach((b, i) => {
      map[b.id] = {
        x: paddingX,
        y: paddingY + i * (nodeHeight + nodeGapY)
      };
    });
    return map;
  }, [visibleBlocks]);
  // Build connections from rules
  const connections = useMemo(() => {
    const conns: {
      fromId: string;
      toId: string;
      ruleId: string;
      ruleIdx: number;
      color: string;
    }[] = [];
    rules.forEach((rule, ruleIdx) => {
      const color = RULE_COLORS[ruleIdx % RULE_COLORS.length];
      rule.conditions.forEach(cond => {
        rule.actions.forEach(act => {
          if (cond.fieldId && act.targetId && cond.fieldId !== act.targetId) {
            conns.push({
              fromId: cond.fieldId,
              toId: act.targetId,
              ruleId: rule.id,
              ruleIdx,
              color
            });
          }
        });
      });
    });
    return conns;
  }, [rules]);
  if (visibleBlocks.length === 0) {
    return <div className="flex items-center justify-center h-full p-6 text-center" data-id="element-1635">
        <div data-id="element-1636">
          <WorkflowIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" data-id="element-1637" />
          <p className="text-sm text-gray-500" data-id="element-1638">
            Add fields to see the flow diagram
          </p>
        </div>
      </div>;
  }
  return <div className="p-3" data-id="element-1639">
      <svg width="100%" viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="overflow-visible" data-id="element-1640">
        <defs data-id="element-1641">
          {RULE_COLORS.map((color, i) => <marker key={i} id={`arrow-${i}`} viewBox="0 0 10 7" refX="10" refY="3.5" markerWidth="8" markerHeight="6" orient="auto-start-reverse" data-id="element-1642">
              <polygon points="0 0, 10 3.5, 0 7" fill={color} data-id="element-1643" />
            </marker>)}
        </defs>

        {/* Connections */}
        {connections.map((conn, i) => {
        const from = nodePositions[conn.fromId];
        const to = nodePositions[conn.toId];
        if (!from || !to) return null;
        const isSelected = conn.ruleId === selectedRuleId;
        const x1 = from.x + nodeWidth;
        const y1 = from.y + nodeHeight / 2;
        const x2 = to.x + nodeWidth;
        const y2 = to.y + nodeHeight / 2;
        const curveX = nodeWidth + 30 + conn.ruleIdx % 4 * 18;
        const d = `M ${x1} ${y1} C ${curveX} ${y1}, ${curveX} ${y2}, ${x2} ${y2}`;
        return <path key={i} d={d} fill="none" stroke={conn.color} strokeWidth={isSelected ? 2.5 : 1.5} strokeOpacity={isSelected ? 1 : 0.3} markerEnd={`url(#arrow-${conn.ruleIdx % RULE_COLORS.length})`} className="transition-all duration-200 cursor-pointer" onClick={() => onSelectRule(conn.ruleId)} data-id="element-1644" />;
      })}

        {/* Nodes */}
        {visibleBlocks.map(block => {
        const pos = nodePositions[block.id];
        if (!pos) return null;
        const color = NODE_COLORS[block.type] || '#6b7280';
        const label = (block.type === 'section' ? block.sectionTitle : block.label || block.name || block.type)?.slice(0, 22) || block.type;
        const isSource = connections.some(c => c.fromId === block.id && c.ruleId === selectedRuleId);
        const isTarget = connections.some(c => c.toId === block.id && c.ruleId === selectedRuleId);
        const isHighlighted = isSource || isTarget;
        return <g key={block.id} data-id="element-1645">
              <rect x={pos.x} y={pos.y} width={nodeWidth} height={nodeHeight} rx={8} fill={isHighlighted ? color : '#ffffff'} stroke={color} strokeWidth={isHighlighted ? 2 : 1} strokeOpacity={isHighlighted ? 1 : 0.5} className="transition-all duration-200" data-id="element-1646" />
              <text x={pos.x + 10} y={pos.y + nodeHeight / 2 - 2} fontSize={11} fontWeight={500} fill={isHighlighted ? '#ffffff' : '#374151'} dominantBaseline="middle" data-id="element-1647">
                {label}
              </text>
              <text x={pos.x + 10} y={pos.y + nodeHeight / 2 + 11} fontSize={9} fill={isHighlighted ? 'rgba(255,255,255,0.7)' : '#9ca3af'} dominantBaseline="middle" data-id="element-1648">
                {block.type}
              </text>
              {/* Color indicator dot */}
              <circle cx={pos.x + nodeWidth - 14} cy={pos.y + nodeHeight / 2} r={4} fill={color} opacity={isHighlighted ? 0 : 0.6} data-id="element-1649" />
            </g>;
      })}
      </svg>

      {/* Legend */}
      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1650">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2" data-id="element-1651">
          Block Types
        </p>
        <div className="flex flex-wrap gap-x-3 gap-y-1.5" data-id="element-1652">
          {Object.entries(visibleBlocks.reduce((acc, b) => {
          acc[b.type] = NODE_COLORS[b.type] || '#6b7280';
          return acc;
        }, {} as Record<string, string>)).map(([type, color]) => <div key={type} className="flex items-center gap-1" data-id="element-1653">
              <div className="w-2.5 h-2.5 rounded-sm" style={{
            backgroundColor: color
          }} data-id="element-1654" />
              <span className="text-[10px] text-gray-500 capitalize" data-id="element-1655">
                {type}
              </span>
            </div>)}
        </div>
      </div>

      {/* Rule connections legend */}
      {rules.length > 0 && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-1656">
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2" data-id="element-1657">
            Rules
          </p>
          <div className="space-y-1" data-id="element-1658">
            {rules.map((rule, idx) => <button key={rule.id} onClick={() => onSelectRule(rule.id)} className={`flex items-center gap-2 w-full text-left px-2 py-1 rounded transition-colors ${selectedRuleId === rule.id ? 'bg-gray-100' : 'hover:bg-gray-50'}`} data-id="element-1659">
                <div className="w-3 h-0.5 rounded-full flex-shrink-0" style={{
            backgroundColor: RULE_COLORS[idx % RULE_COLORS.length],
            height: selectedRuleId === rule.id ? 3 : 2
          }} data-id="element-1660" />
                <span className={`text-[11px] truncate ${selectedRuleId === rule.id ? 'text-gray-900 font-medium' : 'text-gray-500'}`} data-id="element-1661">
                  {rule.name}
                </span>
              </button>)}
          </div>
        </div>}
    </div>;
}
// ── Rules Summary ──
function RulesSummary({
  rules,
  blocks,
  getBlockLabel
}: {
  rules: FormLogicRule[];
  blocks: FormBlock[];
  getBlockLabel: (id: string) => string;
}) {
  if (rules.length === 0) {
    return <div className="flex items-center justify-center h-full p-6 text-center" data-id="element-1662">
        <div data-id="element-1663">
          <SettingsIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" data-id="element-1664" />
          <p className="text-sm text-gray-500" data-id="element-1665">No rules to summarize</p>
        </div>
      </div>;
  }
  const getOperatorLabel = (op: LogicConditionType) => CONDITION_LABELS[op] || op;
  const getActionLabel = (type: LogicActionType) => ACTION_LABELS[type] || type;
  return <div className="p-4 space-y-4" data-id="element-1666">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1667">
        All Rules Summary
      </p>
      {rules.map((rule, idx) => <div key={rule.id} className="rounded-lg border border-gray-200 overflow-hidden" data-id="element-1668">
          <div className="px-3 py-2 flex items-center gap-2" style={{
        backgroundColor: RULE_COLORS[idx % RULE_COLORS.length] + '10',
        borderLeft: `3px solid ${RULE_COLORS[idx % RULE_COLORS.length]}`
      }} data-id="element-1669">
            <span className="text-sm font-medium text-gray-900" data-id="element-1670">
              {rule.name}
            </span>
          </div>
          <div className="p-3 space-y-2 text-xs text-gray-600" data-id="element-1671">
            <div data-id="element-1672">
              <span className="font-semibold text-gray-700" data-id="element-1673">When </span>
              {rule.conditions.map((c, ci) => <span key={c.id} data-id="element-1674">
                  {ci > 0 && <span className="font-semibold text-purple-600" data-id="element-1675">
                      {' '}
                      {rule.conditionMatch === 'all' ? 'AND' : 'OR'}{' '}
                    </span>}
                  <span className="font-medium text-gray-800" data-id="element-1676">
                    {getBlockLabel(c.fieldId)}
                  </span>{' '}
                  <span className="text-gray-500 italic" data-id="element-1677">
                    {getOperatorLabel(c.operator).toLowerCase()}
                  </span>
                  {c.operator !== 'is_empty' && c.operator !== 'is_not_empty' && <>
                        {' '}
                        <span className="font-medium text-gray-800" data-id="element-1678">
                          "{c.value}"
                        </span>
                      </>}
                </span>)}
            </div>
            <div data-id="element-1679">
              <span className="font-semibold text-gray-700" data-id="element-1680">Then </span>
              {rule.actions.map((a, ai) => <span key={a.id} data-id="element-1681">
                  {ai > 0 && <span className="font-semibold text-purple-600" data-id="element-1682"> and </span>}
                  <span className="text-gray-500 italic" data-id="element-1683">
                    {getActionLabel(a.type).toLowerCase()}
                  </span>{' '}
                  <span className="font-medium text-gray-800" data-id="element-1684">
                    {getBlockLabel(a.targetId)}
                  </span>
                  {a.type === 'set_field_value' && a.value && <>
                      {' '}
                      to{' '}
                      <span className="font-medium text-gray-800" data-id="element-1685">
                        "{a.value}"
                      </span>
                    </>}
                  {a.type === 'filter_options' && a.filterValues && a.filterValues.length > 0 && <>
                        {' '}
                        to show{' '}
                        <span className="font-medium text-gray-800" data-id="element-1686">
                          {a.filterValues.length} option
                          {a.filterValues.length !== 1 ? 's' : ''}
                        </span>
                      </>}
                </span>)}
            </div>
          </div>
        </div>)}
    </div>;
}